import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/dom";

export default class extends Controller {
  static targets = ["button", "panel", "input", "option", "label", "hidden"];
  static values = {
    openClass: String,
  };

  connect() {
    this.isOpen = false;
    this.highlightIndex = -1;
    this.cleanupFloating = null;
  }

  // ----- open / close -----

  async open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.panelTarget.hidden = false;
    this.buttonTarget.setAttribute("aria-expanded", "true");
    this.element.classList.add(this.openClassValue || "");

    // focus search
    requestAnimationFrame(() => this.inputTarget?.focus());

    // floating-ui positioning
    this.cleanupFloating = autoUpdate(
      this.buttonTarget,
      this.panelTarget,
      () => {
        computePosition(this.buttonTarget, this.panelTarget, {
          placement: "bottom-start",
          middleware: [offset(4), flip(), shift({ padding: 8 })],
        }).then(({ x, y }) => {
          Object.assign(this.panelTarget.style, {
            left: `${x}px`,
            top: `${y}px`,
          });
        });
      }
    );

    this._bindOutsideEvents();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.panelTarget.hidden = true;
    this.buttonTarget.setAttribute("aria-expanded", "false");
    this.element.classList.remove(this.openClassValue || "");
    this.highlightIndex = -1;
    this._updateHighlighted();

    if (this.cleanupFloating) {
      this.cleanupFloating();
      this.cleanupFloating = null;
    }

    this._unbindOutsideEvents();
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  // ----- outside click / esc -----

  _bindOutsideEvents() {
    this._outsideClickHandler = (event) => {
      if (!this.element.contains(event.target)) this.close();
    };
    this._escHandler = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        this.buttonTarget.focus();
      }
    };
    document.addEventListener("click", this._outsideClickHandler);
    document.addEventListener("keydown", this._escHandler);
  }

  _unbindOutsideEvents() {
    document.removeEventListener("click", this._outsideClickHandler);
    document.removeEventListener("keydown", this._escHandler);
  }

  // ----- selection -----

  selectOption(event) {
    const option = event.currentTarget;
    const value = option.dataset.value;
    const label = option.dataset.label;

    if (this.hasHiddenTarget) this.hiddenTarget.value = value;
    if (this.hasLabelTarget) this.labelTarget.textContent = label;

    this.close();
    this.buttonTarget.focus();
  }

  // ----- filtering -----

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase();

    this.optionTargets.forEach((el) => {
      const label = el.dataset.label.toLowerCase();
      const matches = label.includes(query);
      el.hidden = !matches;
    });

    this.highlightIndex = -1;
    this._updateHighlighted();
  }

  // ----- keyboard handling -----

  buttonKeydown(event) {
    if (!["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) return;

    event.preventDefault();
    if (!this.isOpen) this.open();

    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      this._moveHighlight(event.key === "ArrowDown" ? 1 : -1);
    }
  }

  listKeydown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this._moveHighlight(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this._moveHighlight(-1);
        break;
      case "Enter":
        event.preventDefault();
        if (this.highlightIndex >= 0) {
          this.optionTargets[this.highlightIndex].click();
        }
        break;
      case "Tab":
        this.close();
        break;
    }
  }

  _visibleOptions() {
    return this.optionTargets.filter((el) => !el.hidden);
  }

  _moveHighlight(delta) {
    const visible = this._visibleOptions();
    if (!visible.length) return;

    // map current optionTargets index → visible index
    const currentVisibleIndex =
      this.highlightIndex >= 0
        ? visible.indexOf(this.optionTargets[this.highlightIndex])
        : -1;

    let nextVisibleIndex = currentVisibleIndex + delta;
    if (nextVisibleIndex < 0) nextVisibleIndex = visible.length - 1;
    if (nextVisibleIndex >= visible.length) nextVisibleIndex = 0;

    const nextOption = visible[nextVisibleIndex];
    this.highlightIndex = this.optionTargets.indexOf(nextOption);
    this._updateHighlighted();
    nextOption.focus();
  }

  _updateHighlighted() {
    this.optionTargets.forEach((el, index) => {
      if (index === this.highlightIndex) {
        el.classList.add("combo__option--active");
      } else {
        el.classList.remove("combo__option--active");
      }
    });
  }
}
